// ═══════════════════════════════════════════════════════════════════════════════
// App Root — Routing Configuration
// (BrowserRouter + providers live in main.tsx)
// ═══════════════════════════════════════════════════════════════════════════════

import { useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { USER_ROLE } from '@fdh/shared';
import { ProtectedRoute } from './components/ProtectedRoute';
import { MainAppGate } from './components/layout/MainAppGate';
import { RequireRole } from './components/layout/RequireRole';
import { SettingsAccessGate } from './components/layout/SettingsAccessGate';
import { GenerateCodePage } from './pages/pairing/GenerateCodePage';
import { JoinPairingPage } from './pages/pairing/JoinPairingPage';
import { PairingSuccessPage } from './pages/pairing/PairingSuccessPage';
import { PairingStatusPage } from './pages/pairing/PairingStatusPage';
import { PairCodeInvitePage } from './pages/pairing/PairCodeInvitePage';
import { PairInviteExpiredPage } from './pages/pairing/PairInviteExpiredPage';
import { ImportPreviousPairingPage } from './pages/pairing/ImportPreviousPairingPage';
import { InviteCodeLandingPage } from './pages/auth/InviteCodeLandingPage';
import { ConsentPage } from './pages/consent/ConsentPage';
import {
  TaskListPage,
  TaskPendingReviewPage,
  TaskApprovedListPage,
  EmployerFutureTasksPage,
  EmployerEditAllTasksPage,
  EmployerHistoryTasksPage,
  EmployerOverdueTasksPage,
  TaskFormPage,
  EmployerInventoryNewTaskPage,
  EmployerInventoryEditTaskPage,
  EmployerPickupDropoffTaskPage,
  EmployerMedicationReminderTaskPage,
  TaskReviewPage,
  TodayTodoPage,
  TaskCompletionPage,
  TaskResubmitPage,
  InventoryTaskPage,
} from './pages/task';
import {
  LoginPage,
  RegisterPage,
  ForgotPasswordPage,
  ResetPasswordPage,
  VerifyEmailPage,
  AccountRemovalConfirmPage,
  GoogleCallbackPage,
} from './pages/auth';
import { HomePage } from './pages/home/HomePage';
import { EmployerDashboardPage, HelperHomePage } from './pages/home';
import { SystemMessageListPage } from './pages/systemMessage/SystemMessageListPage';
import { ReceiptAnalyzePage, ReceiptDetailPage, ReceiptEditPage, ReceiptListPage } from './pages/receipt';
import {
  SettlementListPage,
  SettlementDetailPage,
  HelperSettlementConfirmPage,
  HelperSettlementHistoryPage,
  EmployerSettlementPendingReviewPage,
  EmployerSettlementByBudgetDetailPage,
} from './pages/settlement';
import { BudgetListPage, BudgetFormPage } from './pages/budget';
import { FamilyMembersPage, FamilyMemberFormPage } from './pages/family';
import { PinMessageFormPage, PinMessageListPage } from './pages/pinMessage';
import { AdminAuthProvider } from './contexts/AdminAuthContext';
import { AdminPortalRoutes } from './pages/admin';
import { BreakGlassMaintenancePage } from './pages/admin/BreakGlassMaintenancePage';
import { AdminPortalNoIndex } from './components/AdminPortalNoIndex';
import { ADMIN_PORTAL_BASE } from './lib/adminPortalPaths';
import { getMaintenanceBreakGlassPath } from './lib/maintenancePaths';
import { LegalPage } from './pages/legal';
import { FaqPage } from './pages/faq';
import { NotFoundPage } from './pages/NotFoundPage';
import { captureClientChannelFromUrl } from './lib/clientChannel';

const MAINTENANCE_BREAK_GLASS_PATH = getMaintenanceBreakGlassPath();

const ScrollToTopOnRouteChange = () => {
  const { pathname, search } = useLocation();

  useEffect(() => {
    captureClientChannelFromUrl();
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [pathname, search]);

  return null;
};

export const App = () => (
  <>
    <ScrollToTopOnRouteChange />
    <Routes>
    {/* Public Routes */}
    <Route path="/" element={<HomePage />} />
    <Route path="/login" element={<LoginPage />} />
    <Route path="/register" element={<RegisterPage />} />
    <Route path="/forgot-password" element={<ForgotPasswordPage />} />
    <Route path="/reset-password" element={<ResetPasswordPage />} />
    <Route path="/verify-email" element={<VerifyEmailPage />} />
    <Route path="/account-removal/confirm" element={<AccountRemovalConfirmPage />} />
    <Route path="/auth/google/callback" element={<GoogleCallbackPage />} />
    <Route path="/invite-code/:code" element={<InviteCodeLandingPage />} />

    <Route path="/privacy" element={<LegalPage variant="privacy" />} />
    <Route path="/terms" element={<LegalPage variant="terms" />} />
    <Route path="/pics" element={<LegalPage variant="pics" />} />
    <Route path="/faq" element={<FaqPage />} />

    <Route path="/pair-code/:code" element={<PairCodeInvitePage />} />
    <Route path="/pair-invite/expired" element={<PairInviteExpiredPage />} />

    {/* Admin Portal (separate auth; /hfs-admin/*; noindex) */}
    <Route
      path={`${ADMIN_PORTAL_BASE}/*`}
      element={
        <AdminAuthProvider>
          <AdminPortalNoIndex>
            <AdminPortalRoutes />
          </AdminPortalNoIndex>
        </AdminAuthProvider>
      }
    />

    {/* Maintenance break-glass unlock (configurable path; super-admin; skip 2FA) */}
    <Route
      path={MAINTENANCE_BREAK_GLASS_PATH}
      element={
        <AdminAuthProvider>
          <AdminPortalNoIndex>
            <BreakGlassMaintenancePage />
          </AdminPortalNoIndex>
        </AdminAuthProvider>
      }
    />
    {/* Pairing Routes (protected, skip consent — pairing happens before consent) */}
    <Route
      path="/pairing/generate-code"
      element={
        <ProtectedRoute requiredRole="employer" skipConsent>
          <GenerateCodePage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/pairing/join"
      element={
        <ProtectedRoute requiredRole="helper" skipConsent>
          <JoinPairingPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/pairing/success"
      element={
        <ProtectedRoute skipConsent>
          <PairingSuccessPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/pairing/status"
      element={
        <ProtectedRoute skipConsent>
          <PairingStatusPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/settings/*"
      element={
        <ProtectedRoute skipConsent>
          <SettingsAccessGate />
        </ProtectedRoute>
      }
    />

    {/* Consent Page (protected, skip consent check on itself) */}
    <Route
      path="/consent"
      element={
        <ProtectedRoute skipConsent>
          <ConsentPage />
        </ProtectedRoute>
      }
    />

    {/* Main app: authenticated + paired + consented — shell with bottom nav */}
    <Route element={<MainAppGate />}>
      <Route path="/dashboard" element={<EmployerDashboardPage />} />
      <Route path="/home" element={<HelperHomePage />} />
      <Route path="/system-messages" element={<SystemMessageListPage />} />
      <Route path="/stock/latest-home-stock" element={<TaskReviewPage />} />

      <Route
        path="/tasks"
        element={
          <RequireRole role={USER_ROLE.EMPLOYER}>
            <TaskListPage />
          </RequireRole>
        }
      />
      <Route
        path="/tasks/pending-review"
        element={
          <RequireRole role={USER_ROLE.EMPLOYER}>
            <TaskPendingReviewPage />
          </RequireRole>
        }
      />
      <Route
        path="/tasks/approved"
        element={
          <RequireRole role={USER_ROLE.EMPLOYER}>
            <TaskApprovedListPage />
          </RequireRole>
        }
      />
      <Route
        path="/tasks/edit-all"
        element={
          <RequireRole role={USER_ROLE.EMPLOYER}>
            <EmployerEditAllTasksPage />
          </RequireRole>
        }
      />
      <Route
        path="/tasks/history"
        element={
          <RequireRole role={USER_ROLE.EMPLOYER}>
            <EmployerHistoryTasksPage />
          </RequireRole>
        }
      />
      <Route
        path="/tasks/overdue"
        element={
          <RequireRole role={USER_ROLE.EMPLOYER}>
            <EmployerOverdueTasksPage />
          </RequireRole>
        }
      />
      <Route
        path="/tasks/future"
        element={
          <RequireRole role={USER_ROLE.EMPLOYER}>
            <EmployerFutureTasksPage />
          </RequireRole>
        }
      />
      <Route
        path="/tasks/new/inventory"
        element={
          <RequireRole role={USER_ROLE.EMPLOYER}>
            <EmployerInventoryNewTaskPage />
          </RequireRole>
        }
      />
      <Route
        path="/tasks/new/pickup-dropoff"
        element={
          <RequireRole role={USER_ROLE.EMPLOYER}>
            <EmployerPickupDropoffTaskPage />
          </RequireRole>
        }
      />
      <Route
        path="/tasks/new/medication-reminder"
        element={
          <RequireRole role={USER_ROLE.EMPLOYER}>
            <EmployerMedicationReminderTaskPage />
          </RequireRole>
        }
      />
      <Route
        path="/tasks/new"
        element={
          <RequireRole role={USER_ROLE.EMPLOYER}>
            <TaskFormPage />
          </RequireRole>
        }
      />
      <Route
        path="/tasks/:id/edit/inventory"
        element={
          <RequireRole role={USER_ROLE.EMPLOYER}>
            <EmployerInventoryEditTaskPage />
          </RequireRole>
        }
      />
      <Route
        path="/tasks/groups/:groupId/edit/pickup-dropoff"
        element={
          <RequireRole role={USER_ROLE.EMPLOYER}>
            <EmployerPickupDropoffTaskPage />
          </RequireRole>
        }
      />
      <Route
        path="/tasks/groups/:groupId/edit/medication-reminder"
        element={
          <RequireRole role={USER_ROLE.EMPLOYER}>
            <EmployerMedicationReminderTaskPage />
          </RequireRole>
        }
      />
      <Route
        path="/tasks/:id/edit"
        element={
          <RequireRole role={USER_ROLE.EMPLOYER}>
            <TaskFormPage />
          </RequireRole>
        }
      />
      <Route
        path="/task-instances/:id/review"
        element={
          <RequireRole role={USER_ROLE.EMPLOYER}>
            <TaskReviewPage />
          </RequireRole>
        }
      />

      <Route
        path="/today"
        element={
          <RequireRole role={USER_ROLE.HELPER}>
            <TodayTodoPage />
          </RequireRole>
        }
      />
      <Route
        path="/tasks/complete/:id"
        element={
          <RequireRole role={USER_ROLE.HELPER}>
            <TaskCompletionPage />
          </RequireRole>
        }
      />
      <Route
        path="/tasks/resubmit/:id"
        element={
          <RequireRole role={USER_ROLE.HELPER}>
            <TaskResubmitPage />
          </RequireRole>
        }
      />
      <Route
        path="/tasks/inventory/:id"
        element={
          <RequireRole role={USER_ROLE.HELPER}>
            <InventoryTaskPage />
          </RequireRole>
        }
      />
      <Route
        path="/receipts/analyze"
        element={
          <RequireRole role={USER_ROLE.HELPER}>
            <ReceiptAnalyzePage />
          </RequireRole>
        }
      />
      <Route
        path="/receipts/:id/edit"
        element={
          <RequireRole role={USER_ROLE.HELPER}>
            <ReceiptEditPage />
          </RequireRole>
        }
      />

      <Route path="/receipts" element={<ReceiptListPage />} />
      <Route path="/receipts/:id" element={<ReceiptDetailPage />} />

      <Route
        path="/budgets/new"
        element={
          <RequireRole role={USER_ROLE.EMPLOYER}>
            <BudgetFormPage />
          </RequireRole>
        }
      />
      <Route
        path="/budgets/:budgetId/edit"
        element={
          <RequireRole role={USER_ROLE.EMPLOYER}>
            <BudgetFormPage />
          </RequireRole>
        }
      />
      <Route
        path="/budgets"
        element={
          <RequireRole role={USER_ROLE.EMPLOYER}>
            <BudgetListPage />
          </RequireRole>
        }
      />

      <Route
        path="/settlements/history"
        element={
          <RequireRole role={USER_ROLE.HELPER}>
            <HelperSettlementHistoryPage />
          </RequireRole>
        }
      />
      <Route
        path="/settlements/confirm"
        element={
          <RequireRole role={USER_ROLE.HELPER}>
            <HelperSettlementConfirmPage />
          </RequireRole>
        }
      />
      <Route path="/settlements" element={<SettlementListPage />} />
      <Route
        path="/settlements/pending-review"
        element={
          <RequireRole role={USER_ROLE.EMPLOYER}>
            <EmployerSettlementPendingReviewPage />
          </RequireRole>
        }
      />
      <Route path="/settlements/by-budget/:budgetId" element={<EmployerSettlementByBudgetDetailPage />} />
      <Route path="/settlements/:id" element={<SettlementDetailPage />} />

      <Route
        path="/family-members/new"
        element={
          <RequireRole role={USER_ROLE.EMPLOYER}>
            <FamilyMemberFormPage />
          </RequireRole>
        }
      />
      <Route
        path="/family-members/:memberId/edit"
        element={
          <RequireRole role={USER_ROLE.EMPLOYER}>
            <FamilyMemberFormPage />
          </RequireRole>
        }
      />
      <Route path="/family-members" element={<FamilyMembersPage />} />

      <Route path="/task-instances/:id" element={<TaskReviewPage />} />

      <Route
        path="/pin-messages"
        element={
          <RequireRole role={USER_ROLE.EMPLOYER}>
            <PinMessageListPage />
          </RequireRole>
        }
      />
      <Route
        path="/pin-messages/new"
        element={
          <RequireRole role={USER_ROLE.EMPLOYER}>
            <PinMessageFormPage />
          </RequireRole>
        }
      />
      <Route
        path="/pin-messages/:id/edit"
        element={
          <RequireRole role={USER_ROLE.EMPLOYER}>
            <PinMessageFormPage />
          </RequireRole>
        }
      />
      <Route
        path="/pairing/import-previous"
        element={
          <RequireRole role={USER_ROLE.EMPLOYER}>
            <ImportPreviousPairingPage />
          </RequireRole>
        }
      />
    </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  </>
);
